import type { ApolloError } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";
import { flatten, groupBy } from "lodash";
import { useCallback, useEffect, useState } from "react";

import type { IPermissionsContext } from "../types";
import type { GetUserGroupsAtDashboardQuery } from "gql/graphql";
import { GET_USER_ORGANIZATIONS_GROUPS } from "pages/to-do/queries";
import { Logger } from "utils/logger";

type TGroup =
  GetUserGroupsAtDashboardQuery["me"]["groups"]["edges"][number]["node"];
type TGroupsPermissions = Record<string, string[]>;

const FIRST = 100;

interface IAllGroupPermissionsResponse {
  error: ApolloError | undefined;
  loading: boolean;
  permissions: TGroupsPermissions;
  permissionMethods: {
    can: (action: string) => boolean;
    canMutate: (action: string) => boolean;
    canResolve: (action: string) => boolean;
    refresh: () => Promise<void>;
  };
  organizations: IPermissionsContext["organizations"];
}

const useAllGroupPermissions = (): IAllGroupPermissionsResponse => {
  const [
    getAllPermissions,
    { data: userData, loading, error, fetchMore: fetchMoreGroups },
  ] = useLazyQuery(GET_USER_ORGANIZATIONS_GROUPS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((err): void => {
        Logger.warning("An error occurred fetching user groups", err);
      });
    },
    variables: { first: FIRST },
  });

  const [allGroupsPermissions, setAllGroupsPermissions] =
    useState<TGroupsPermissions>({});

  const [organizations, setOrganizations] = useState<
    IPermissionsContext["organizations"]
  >([]);

  useEffect((): void => {
    if (userData !== undefined) {
      const groups = userData.me.groups.edges.map((edge): TGroup => edge.node);

      const groupsByOrg = groupBy(
        groups,
        (group): string => group.organization,
      );

      const organizationsWithGroups = Object.entries(groupsByOrg).map(
        ([
          orgName,
          orgGroups,
        ]): IPermissionsContext["organizations"][number] => ({
          groups: orgGroups,
          name: orgName,
        }),
      );

      const groupPermissionsMap = groups.reduce<TGroupsPermissions>(
        (acc, group): TGroupsPermissions => ({
          ...acc,
          [group.name]: group.permissions,
        }),
        {},
      );

      setOrganizations(organizationsWithGroups);
      setAllGroupsPermissions(groupPermissionsMap);
    }
  }, [userData]);

  useEffect((): void => {
    if (userData?.me.groups.pageInfo.hasNextPage === true) {
      void fetchMoreGroups({
        variables: {
          after: userData.me.groups.pageInfo.endCursor,
          first: FIRST,
        },
      });
    }
  }, [userData, fetchMoreGroups]);

  const refresh = useCallback(async (): Promise<void> => {
    await getAllPermissions({ variables: { first: FIRST } });
  }, [getAllPermissions]);

  const can = useCallback(
    (permission: string): boolean =>
      flatten(Object.values(allGroupsPermissions)).includes(permission),
    [allGroupsPermissions],
  );

  const canMutate = useCallback(
    (action: string): boolean =>
      can(`integrates_api_mutations_${action}_mutate`),
    [can],
  );

  const canResolve = useCallback(
    (action: string): boolean =>
      can(`integrates_api_resolvers_${action}_resolve`),
    [can],
  );

  const hasPendingPages = userData?.me.groups.pageInfo.hasNextPage === true;

  return {
    error,
    loading: loading || hasPendingPages,
    organizations,
    permissionMethods: {
      can,
      canMutate,
      canResolve,
      refresh,
    },
    permissions: allGroupsPermissions,
  };
};

export { useAllGroupPermissions };
